/**
 * <mj-collection-toolbar> -- the filter and sort bar, and the filter drawer it opens.
 *
 * Neither control depends on this file to work.
 *
 * Sorting is a real <select> inside a GET form whose submit button is off-screen rather
 * than removed; all this adds is submitting on change, so nobody has to find that button.
 *
 * The drawer opens through :target -- the "All Filters" link names the drawer's id, and
 * CSS slides it in when the URL points at it. So the one thing a shopper most needs from
 * the drawer survives this file failing to run, which is not hypothetical: it did fail,
 * silently, for exactly the reason described below.
 *
 * What this adds is the part CSS cannot do: moving focus into the panel and back out,
 * holding it there while the panel is open, closing on Escape, and stopping the page
 * behind it scrolling.
 */

/*
 * Wrapped, because theme scripts are classic <script defer> tags and therefore share one
 * global scope. This file used to declare FOCUSABLE at the top level and so did
 * mj-header.js, which loads first on every page -- the second declaration threw
 * "Identifier 'FOCUSABLE' has already been declared", the whole file stopped evaluating,
 * and customElements.define never ran. Nothing in here reaches the global scope now.
 */
(() => {
  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  class MjCollectionToolbar extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('[data-mj-sort-select]');
      this.sortForm = this.querySelector('[data-mj-sort]');
      this.sortValue = this.querySelector('[data-mj-sort-value]');

      if (this.select && this.sortForm) {
        this.select.addEventListener('change', () => this.#sort());
      }

      this.#setUpDrawer();
    }

    disconnectedCallback() {
      this.#unlockScroll();
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
    }

    /* ---- sort ------------------------------------------------------------ */

    #sort() {
      const chosen = this.select.options[this.select.selectedIndex];

      // Repaint before navigating: on a slow collection the old value would otherwise sit
      // there, reading as if the choice had not registered.
      if (this.sortValue && chosen) this.sortValue.textContent = chosen.textContent.trim();

      if (typeof this.sortForm.requestSubmit === 'function') {
        this.sortForm.requestSubmit();
      } else {
        this.sortForm.submit();
      }
    }

    /* ---- drawer ---------------------------------------------------------- */

    #setUpDrawer() {
      this.drawer = this.querySelector('[data-mj-filters]');
      this.opener = this.querySelector('[data-mj-filters-open]');
      if (!this.drawer || !this.opener || !this.drawer.id) return;

      this.panel = this.drawer.querySelector('.mj-filters__panel');
      this.closer = this.drawer.querySelector('[data-mj-filters-close][role="button"]');

      // Cancel the link's navigation and drive it from a class instead, so the drawer's
      // id never reaches the address bar. Without a script the same links still work,
      // through :target -- that is the whole reason they are links.
      this.opener.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      });

      this.drawer.querySelectorAll('[data-mj-filters-close]').forEach((el) => {
        el.addEventListener('click', (event) => {
          event.preventDefault();
          this.close();
        });
      });

      this.onKeydown = (event) => {
        if (!this.#isOpen()) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
        } else if (event.key === 'Tab') {
          this.#holdFocus(event);
        }
      };
      document.addEventListener('keydown', this.onKeydown);

      this.#watchForChanges();

      // Arrived on a link that names the drawer -- either from elsewhere, or because the
      // page loaded before this script did. Take the hash back out of the address bar and
      // carry on with the class.
      if (window.location.hash === `#${this.drawer.id}`) {
        history.replaceState(null, '', window.location.pathname + window.location.search);
        this.open();
      }
    }

    #isOpen() {
      return this.drawer.classList.contains('mj-filters--open');
    }

    open() {
      if (this.#isOpen()) return;
      this.drawer.classList.add('mj-filters--open');
      this.opener.setAttribute('aria-expanded', 'true');
      this.#lockScroll();

      // preventScroll, because the panel is still sliding: without it the browser jumps
      // to wherever the element currently sits, which is off the left edge.
      if (this.closer) this.closer.focus({ preventScroll: true });
    }

    close() {
      if (!this.#isOpen()) return;
      this.drawer.classList.remove('mj-filters--open');
      this.opener.setAttribute('aria-expanded', 'false');
      this.#unlockScroll();
      this.opener.focus({ preventScroll: true });
    }

    #holdFocus(event) {
      if (!this.panel) return;

      const items = Array.from(this.panel.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    /*
     * "Show 131 Rings" has to answer for the boxes ticked right now, and only the server can
     * count that. So every change re-asks it -- but for this section alone, through the
     * section rendering API, rather than for the whole collection page. The reply carries
     * the same two strings this drawer already renders, so they are lifted across verbatim
     * instead of being parsed for a number.
     *
     * Nothing else in the reply is used: replacing the groups would throw away the ticks the
     * shopper is in the middle of making, and the scroll position with them.
     */
    #watchForChanges() {
      this.form = this.drawer.querySelector('[data-mj-filters-form]');
      this.submitLabel = this.drawer.querySelector('[data-mj-filters-submit-label]');
      this.headerCount = this.drawer.querySelector('[data-mj-filters-count]');
      if (!this.form || !this.dataset.sectionId) return;

      const recount = () => this.#recount();

      this.selected = this.drawer.querySelector('[data-mj-filters-selected]');
      this.chipList = this.drawer.querySelector('[data-mj-filters-chips]');
      this.selectedCount = this.drawer.querySelector('[data-mj-filters-selected-count]');

      this.form.addEventListener('change', (event) => {
        // Settle the price pair before counting, or the count answers for a range the
        // fields are about to stop showing.
        if (event.target.dataset?.mjPrice) this.#settlePrice(event.target);
        this.#renderChips();
        recount();
      });

      // The price fields are typed into, so they would otherwise only recount on blur.
      this.form.addEventListener('input', (event) => {
        if (event.target.type !== 'number') return;

        // Only the bounds here, never the value: someone typing their way to 500 with a
        // maximum of 300 would otherwise be cut off at the 5.
        this.#syncPriceBounds();

        clearTimeout(this.typing);
        this.typing = setTimeout(() => {
          // An impossible range matches nothing, and "Show 0 Rings" flashing up midway
          // through typing reads as a broken filter rather than as an unfinished one.
          if (this.#priceInputs().every((input) => input.checkValidity())) recount();
        }, 400);
      });

      this.#syncPriceBounds();
      this.#renderChips();

      /*
       * Clear All empties the form where it stands instead of following its own href.
       * Following it reloads the collection, and a reload takes the drawer with it -- the
       * shopper asked to clear the filters, not to be put back on the page behind them.
       * The link still navigates with no script, which is why it is a link.
       */
      this.drawer.querySelector('[data-mj-filters-clear]')?.addEventListener('click', (event) => {
        event.preventDefault();
        this.#clearFilters();
      });
    }

    #clearFilters() {
      for (const field of this.form.elements) {
        // The sort carries across: it is not a filter, and clearing it would silently put
        // the shopper back on the collection's default order.
        if (field.type === 'hidden' || !field.name) continue;

        if (field.type === 'checkbox' || field.type === 'radio') field.checked = false;
        else field.value = '';
      }

      // Both ends are open again, so the bounds they put on each other have to come off.
      this.#syncPriceBounds();
      this.#renderChips();
      this.#recount();
    }

    /*
     * The row under the drawer's header: one chip per thing currently ticked, each a
     * button that unticks it. Built from the form rather than from the page's own filters
     * because the drawer applies nothing until Show is pressed -- what the shopper has
     * chosen a second ago is only in these controls.
     *
     * Rebuilt whole on every change. The list is a handful of chips, so there is nothing
     * to gain from patching it, and rebuilding cannot drift out of step with the form.
     */
    #renderChips() {
      if (!this.chipList || !this.selected) return;

      const chips = [];

      for (const input of this.form.querySelectorAll('[data-mj-chip]')) {
        if (input.checked) chips.push({ label: input.dataset.mjChip, group: input.dataset.mjChipGroup, input });
      }

      for (const pair of this.form.querySelectorAll('[data-mj-price-pair]')) {
        const min = pair.querySelector('[data-mj-price="min"]');
        const max = pair.querySelector('[data-mj-price="max"]');
        if (!min || !max || (min.value === '' && max.value === '')) continue;

        const money = (v) => `${this.dataset.currencySymbol || ''}${Number(v).toLocaleString()}`;
        let label;
        if (min.value !== '' && max.value !== '') label = `${money(min.value)} – ${money(max.value)}`;
        else if (min.value !== '') label = `From ${money(min.value)}`;
        else label = `Up to ${money(max.value)}`;

        chips.push({ label, group: this.dataset.priceLabel || 'Price', price: [min, max] });
      }

      this.chipList.replaceChildren();

      for (const chip of chips) {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'mj-filters__chip';
        button.setAttribute('aria-label', `${this.dataset.removeLabel || 'Remove'} ${chip.group}: ${chip.label}`);
        button.textContent = chip.label;

        // The same glyph the markup uses, cloned rather than written out as a string.
        const glyph = this.drawer.querySelector('[data-mj-chip-glyph]')?.content?.firstElementChild;
        if (glyph) button.append(glyph.cloneNode(true));

        button.addEventListener('click', () => {
          if (chip.input) {
            chip.input.checked = false;
          } else {
            for (const field of chip.price) field.value = '';
          }
          this.#syncPriceBounds();
          this.#renderChips();
          this.#recount();
        });

        item.append(button);
        this.chipList.append(item);
      }

      this.selected.hidden = chips.length === 0;
      if (this.selectedCount) {
        this.selectedCount.textContent = chips.length > 0 ? `(${chips.length})` : '';
      }
    }

    #priceInputs() {
      return Array.from(this.form.querySelectorAll('[data-mj-price]'));
    }

    /*
     * Each end of the range bounds the other: the lowest price cannot be asked to sit
     * above the highest. Written onto the inputs rather than only checked in script, so
     * the browser enforces it for free -- the steppers stop at the crossover and the
     * field reports itself invalid, which is what the recount reads before counting.
     *
     * The collection's own ceiling is remembered first, because `max` on the Min field is
     * about to be rewritten and that attribute is where the ceiling was. A blank end
     * means "no bound" rather than zero, so it restores the ceiling instead of clamping.
     */
    #syncPriceBounds() {
      for (const pair of this.form.querySelectorAll('[data-mj-price-pair]')) {
        const min = pair.querySelector('[data-mj-price="min"]');
        const max = pair.querySelector('[data-mj-price="max"]');
        if (!min || !max) continue;

        if (min.dataset.mjCeiling === undefined) {
          min.dataset.mjCeiling = min.getAttribute('max') ?? '';
        }

        const ceiling = min.dataset.mjCeiling;
        if (max.value === '' || ceiling === '') {
          min.setAttribute('max', max.value === '' ? ceiling : max.value);
        } else {
          min.setAttribute('max', String(Math.min(Number(ceiling), Number(max.value))));
        }

        max.setAttribute('min', min.value === '' ? '0' : min.value);
      }
    }

    /*
     * Called when a price field is done being edited. The end the shopper just left is
     * the one that stays: it is what they said most recently, so the other end gives way.
     * Clamping the field they are holding would quietly throw away what they just typed.
     *
     * The other end is cleared rather than matched. Typing a minimum of 500 against a
     * maximum of 300 reads as "at least 500", and an open upper bound says that; moving
     * the maximum to 500 as well would say "exactly 500", which finds nothing and looks
     * like the filter broke.
     */
    #settlePrice(input) {
      const pair = input.closest('[data-mj-price-pair]');
      const min = pair?.querySelector('[data-mj-price="min"]');
      const max = pair?.querySelector('[data-mj-price="max"]');
      if (!min || !max) return;

      const ceiling = Number(min.dataset.mjCeiling || Infinity);
      if (input.value !== '' && Number(input.value) > ceiling) input.value = String(ceiling);

      if (min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value)) {
        if (input === min) max.value = '';
        else min.value = '';
      }

      this.#syncPriceBounds();
    }

    async #recount() {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(this.form).entries()) {
        // An empty price field means "no bound", and sending it as an empty parameter makes
        // Shopify treat the range as invalid rather than open.
        if (value !== '') params.append(key, value);
      }
      params.set('section_id', this.dataset.sectionId);

      // Only the last answer matters; a slower earlier one would overwrite it.
      this.inflight?.abort();
      this.inflight = new AbortController();

      try {
        const response = await fetch(`${this.form.action}?${params}`, { signal: this.inflight.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const fresh = new DOMParser().parseFromString(await response.text(), 'text/html');

        const label = fresh.querySelector('[data-mj-filters-submit-label]');
        if (label && this.submitLabel) this.submitLabel.textContent = label.textContent.trim();

        const count = fresh.querySelector('[data-mj-filters-count]');
        if (count && this.headerCount) this.headerCount.textContent = count.textContent.trim();
      } catch (error) {
        if (error.name === 'AbortError') return;

        // Rather than leave a number that is now wrong, say nothing about how many.
        const pending = this.dataset.pendingLabel;
        if (pending && this.submitLabel) this.submitLabel.textContent = pending;
      }
    }

    #lockScroll() {
      if (this.previousOverflow !== undefined) return;
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
    }

    #unlockScroll() {
      if (this.previousOverflow === undefined) return;
      document.body.style.overflow = this.previousOverflow;
      this.previousOverflow = undefined;
    }
  }

  if (!customElements.get('mj-collection-toolbar')) {
    customElements.define('mj-collection-toolbar', MjCollectionToolbar);
  }
})();
